import { OrderReportResponse, OrderDetails } from 'api/orderManagement/types'
import { isAxiosError } from 'axios'
import { chowlyApi } from 'config'
import { generatePath } from 'react-router-dom'
import { toast } from 'react-toastify'

import client from '../chowly'

export const getOrderReport = async ({
  partnerId,
  link = null,
  limit = 15,
  filter = {},
}: {
  partnerId: string
  link: string | null
  limit: number
  filter: {
    status?: string
    platforms?: string[] | string
    order_type?: string[] | string
    customer_name?: string
    aasm_state?: string[] | string
  }
}) => {
  const page = 1

  const endpoint = link || generatePath(chowlyApi.orderManagement)
  filter.platforms = Array.isArray(filter.platforms) ? filter.platforms.join(',') : filter.platforms

  const params: any = !link ? { 
    partner_id: partnerId, 
    page, 
    limit 
  } : undefined;
  
  if (!link) {
    params.filter = { ...filter };
    
    // Handle aasm_state specially
    if (filter.aasm_state) {
      if (Array.isArray(filter.aasm_state)) {
        // For arrays, use the filter[aasm_state][] format
        delete params.filter.aasm_state;
        filter.aasm_state.forEach((state: string) => {
          params[`filter[aasm_state][]`] = params[`filter[aasm_state][]`] || [];
          params[`filter[aasm_state][]`].push(state);
        });
      }
    }
  }
  
  const { data } = await client.get<OrderReportResponse>(endpoint, { params })

  return data
}

export const refundOrder = async (
  orderId: number,
  isFullRefund = true,
  refundAmount?: number
): Promise<void> => {
  const endpoint = generatePath(chowlyApi.refundOrder)

  try {
    const payload = {
      id: orderId,
      is_full_refund: isFullRefund,
      refund_amount: !isFullRefund && refundAmount ? refundAmount : undefined
    }

    const response = await client.put<void>(endpoint, payload)

    if (response.status === 204) {
      const refundType = isFullRefund ? 'full' : 'partial'
      const amountText = !isFullRefund && refundAmount ? ` of $${refundAmount.toFixed(2)}` : ''
      toast.success(`Order ${orderId} ${refundType} refund${amountText} processed successfully.`)
    }
  } catch (error: any) {
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 400:
          toast.error(
            `Order ${orderId} is not refundable. Reason: ${data?.message || 'Unknown reason'}.`,
          )
          break
        case 404:
          toast.error(`Order ${orderId} not found.`)
          break
        case 403:
          toast.error(`Forbidden: this order cannot be refunded: ${orderId}.`)
          break
        default:
          toast.error(`An unexpected error occurred: ${status}`)
      }
    } else {
      toast.error(`A network error occurred: ${error.message}`)
    }
  }
}

export const cancelOrder = async (orderId: number): Promise<void> => {
  const endpoint = generatePath(chowlyApi.cancelOrder)

  try {
    const response = await client.put<void>(endpoint, { id: orderId })

    if (response.status === 204) {
      toast.success(`Order ${orderId} cancelled successfully.`)
    }
  } catch (error: any) {
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 400:
          toast.error(
            `Order ${orderId} is not cancellable. Reason: ${data?.message || 'Unknown reason'}.`,
          )
          break
        case 404:
          toast.error(`Order ${orderId} not found.`)
          break
        case 403:
          toast.error(`Forbidden: this order cannot be cancelled.`)
          break
        default:
          toast.error(`An unexpected error occurred: ${status}`)
      }
    } else {
      toast.error(`A network error occurred: ${error.message}`)
    }
  }
}

export const markOrderReadyForPickup = async (
  orderId: number,
): Promise<{ ready_at: string } | undefined> => {
  const endpoint = chowlyApi.readyForPickup
  const payload = {
    id: orderId.toString(),
  }

  try {
    const response = await client.put<{ ready_at: string }>(endpoint, payload)

    if (response.status === 200) {
      toast.success(`Order ${orderId} marked as ready for pickup successfully.`)
      return response.data
    }
  } catch (error: unknown) {
    if (isAxiosError(error)) {
      toast.error(
        `Could not notify DoorDash. Delivery may be delayed. ${error?.response?.data?.error}`,
      )
    } else {
      toast.error('An unexpected error occurred.')
    }
  }

  return undefined
}

export const getOrderDetails = async (orderId: string) => {
  const endpoint = generatePath(chowlyApi.orderDetails, { id: orderId })
  const response = await client.get<{ data: OrderDetails; included?: any[] }>(endpoint, {
    params: {
      include: 'delivery,payment.payment_provider'
    }
  })
  return {
    data: response.data.data,
    included: response.data.included || []
  }
}
